import React, { useRef, useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { useProductosMaterialContext } from 'context';
import { useAmbienteEcf, ambienteEcfValido } from 'lib/ambienteEcf';
import { FP } from 'components/financepro';
import DocumentoEmpresarialPreview from 'facturapos/DocumentoEmpresarialPreview';
import {
  generarHtmlDocumentoEmpresarial,
  fechaEmisionDgii,
  formatoFechaLargo,
  money,
  resolverFechaVencimiento,
} from 'facturapos/documentoEmpresarial';
import { useEmpresaFiscal } from "lib/empresaFiscal";

const toNumber = (value) => parseFloat(String(value ?? '').replace(/,/g, '')) || 0;

/**
 * Vista previa e impresión del e-CF de Exportaciones (E46).
 */
const VistaExportacion = ({
  open,
  onClose,
  compras = [],
  ncf = '',
  emisionData = {},
  empresa = {},
  rncComprador = '',
  razonSocialComprador = '',
}) => {
  const { controller, showError } = useProductosMaterialContext();
  const {
    nombreNegocio,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;

  const nombreEmpresaContexto =
    typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : '';
  const direccionEmpresaContexto =
    typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim()
      ? direccionEmpresaCtx.trim()
      : '';
  const rncEmpresaContexto =
    typeof rncEmpresaCtx === 'string' && rncEmpresaCtx.trim() ? rncEmpresaCtx.trim() : '';

  // Multi-empresa: el emisor es la empresa fiscal elegida (llega en `empresa`).
  const { activo: multiempresaFiscal } = useEmpresaFiscal();
  const nombreEmpresa =
    (multiempresaFiscal && empresa.nombre) || nombreEmpresaContexto || empresa.nombre || 'Empresa';
  const direccionEmpresa =
    (multiempresaFiscal && empresa.direccion) || direccionEmpresaContexto || empresa.direccion || '';
  const rncEmpresa = (multiempresaFiscal && empresa.rnc) || rncEmpresaContexto || empresa.rnc || '';

  const printRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState('');

  const codigoSeguridad = String(emisionData?.codigo_seguridad || '').trim();
  const fechaFirma = String(emisionData?.fecha_firma || '').trim();

  const subtotal = useMemo(
    () => compras.reduce((s, c) => s + toNumber(c.total), 0),
    [compras]
  );
  const totalItbis = useMemo(
    () => compras.reduce((s, c) => s + toNumber(c.itbis), 0),
    [compras]
  );
  const totalRetencion = useMemo(
    () => compras.reduce((s, c) => s + toNumber(c.retencion), 0),
    [compras]
  );
  // MontoTotal del e-CF / QR (DGII no descuenta ISR retenido).
  const montoDocumento = useMemo(
    () => toNumber(emisionData?.monto_total) || subtotal + totalItbis,
    [emisionData?.monto_total, subtotal, totalItbis]
  );
  const montoTotal = useMemo(
    () => Math.round((subtotal + totalItbis - totalRetencion) * 100) / 100,
    [subtotal, totalItbis, totalRetencion]
  );

  const rncCompradorDigits = String(rncComprador || compras[0]?.rnc || '').replace(/-/g, '');
  const identificadorExtranjero = rncCompradorDigits.replace(/[^A-Za-z0-9]/g, '').slice(0, 20);

  const suplidorNombre =
    razonSocialComprador ||
    String(compras[0]?.suplidor_nombre || '').trim() ||
    String(compras[0]?.suplidor || '').trim();

  const numeroDesembolso = compras[0]?.numero || compras[0]?.id_compra || compras[0]?.id || '';
  const fechaCompra = compras[0]?.fecha || compras[0]?.fecha_ecf || '';
  const fechaDocumento = formatoFechaLargo(fechaCompra) || formatoFechaLargo(new Date());

  const fechaVencimiento = useMemo(
    () => resolverFechaVencimiento(emisionData),
    [emisionData]
  );

  // Ambiente de la emisión si viene; si no, el del tenant (varinicial.ambienteecf).
  const ambienteTenant = useAmbienteEcf();
  const ambienteQr = ambienteEcfValido(emisionData?.ambiente) || ambienteTenant;

  const qrUrl = useMemo(() => {
    if (!open || !ncf || !ambienteQr) return '';
    const rncEmisor = (rncEmpresa || '').replace(/-/g, '');
    const fechaEmisionDgiiFmt = fechaEmisionDgii(fechaFirma || fechaCompra);
    return (
      `https://ecf.dgii.gov.do/${ambienteQr}/ConsultaTimbre?` +
      `RncEmisor=${encodeURIComponent(rncEmisor)}` +
      `&RncComprador=${encodeURIComponent(rncCompradorDigits)}` +
      `&ENCF=${encodeURIComponent(String(ncf).toUpperCase())}` +
      `&FechaEmision=${encodeURIComponent(fechaEmisionDgiiFmt)}` +
      `&MontoTotal=${encodeURIComponent(montoDocumento.toFixed(2))}` +
      `&FechaFirma=${encodeURIComponent(fechaFirma)}` +
      `&CodigoSeguridad=${encodeURIComponent(codigoSeguridad)}`
    );
  }, [
    open,
    ncf,
    rncEmpresa,
    rncCompradorDigits,
    fechaFirma,
    fechaCompra,
    montoDocumento,
    codigoSeguridad,
    ambienteQr,
  ]);

  useEffect(() => {
    if (!open || !qrUrl) {
      setQrDataUrl('');
      return undefined;
    }
    let cancelado = false;
    QRCode.toDataURL(qrUrl, { margin: 1, width: 220 })
      .then((url) => {
        if (!cancelado) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelado) setQrDataUrl('');
      });
    return () => {
      cancelado = true;
    };
  }, [open, qrUrl]);

  const items = useMemo(
    () =>
      compras.map((c) => ({
        codigo: String(c.numero || c.id || ''),
        descripcion: (c.descripcion || 'Exportación').trim(),
        cantidad: 1,
        precio: toNumber(c.total),
        descuento: 0,
        subtotal: toNumber(c.total),
      })),
    [compras]
  );

  const filasTotales = useMemo(() => {
    const filas = [
      { label: 'Subtotal', value: subtotal },
    ];
    if (totalItbis > 0) {
      filas.push({ label: 'ITBIS', value: totalItbis });
    }
    if (totalRetencion > 0) {
      filas.push({
        label: 'ISR retenido 27%',
        value: `−${money(totalRetencion)}`,
        danger: true,
      });
    }
    return filas;
  }, [subtotal, totalItbis, totalRetencion]);

  const parteSuplidor =
    suplidorNombre || identificadorExtranjero
      ? {
          titulo: 'Comprador',
          nombre: suplidorNombre || identificadorExtranjero,
          lineas: identificadorExtranjero && suplidorNombre ? [`ID: ${identificadorExtranjero}`] : [],
        }
      : null;

  const metaExtra = useMemo(() => {
    const extra = [];
    if (ncf) extra.push({ label: 'NCF', value: ncf });
    //if (emisionData?.estado) extra.push({ label: 'Estado DGII', value: emisionData.estado });
    return extra;
  }, [ncf, fechaVencimiento, emisionData?.estado]);

  const handleImprimir = () => {
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    const htmlContent = generarHtmlDocumentoEmpresarial({
      tituloDocumento: 'COMPROBANTE PARA EXPORTACIONES',
      numero: numeroDesembolso || ncf,
      documento: numeroDesembolso ? 'DS' : '',
      fecha: fechaDocumento,
      fechaVencimiento,
      metaExtra,
      parte: parteSuplidor,
      items,
      filasTotales,
      totalLabel: 'TOTAL A PAGAR',
      totalValue: montoTotal,
      qrDataUrl,
      codigoSeguridad,
      fechaFirma,
      nombreEmpresa,
      direccionEmpresa,
      rncEmpresa,
      mostrarInfoEmpresa: true,
      mensajeGracias: '',
      leyendaPiePagina: 'Documento generado electrónicamente',
    });
    ventana.document.write(htmlContent);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => {
      ventana.print();
      ventana.close();
    }, 250);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          maxHeight: '92vh',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.6,
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.2,
            bgcolor: FP.color.navySoft,
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography
            sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.98rem', lineHeight: 1.2 }}
          >
            Compra electrónica {numeroDesembolso ? `DS-${numeroDesembolso}` : ncf || '—'}
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem' }}>
            Vista previa · Formato empresarial
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button
          startIcon={<CloseIcon />}
          onClick={onClose}
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: FP.color.textSecondary,
            borderRadius: 1.4,
            '&:hover': { bgcolor: FP.color.surfaceAlt },
          }}
        >
          Cerrar
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: FP.color.surface }}>
        <DocumentoEmpresarialPreview
          printRef={printRef}
          mostrarInfoEmpresa
          nombreEmpresa={nombreEmpresa}
          direccionEmpresa={direccionEmpresa}
          rncEmpresa={rncEmpresa}
          tituloDocumento="COMPROBANTE PARA EXPORTACIONES"
          numero={numeroDesembolso || ncf}
          documento={numeroDesembolso ? 'DS' : ''}
          fecha={fechaDocumento}
          fechaVencimiento={fechaVencimiento}
          metaExtra={metaExtra}
          parte={parteSuplidor}
          items={items}
          filasTotales={filasTotales}
          totalLabel="TOTAL A PAGAR"
          totalValue={montoTotal}
          observaciones=""
          qrDataUrl={qrDataUrl}
          codigoSeguridad={codigoSeguridad}
          fechaFirma={fechaFirma}
          mensajeGracias=""
          footerNote="Documento generado electrónicamente"
          varianteTabla="listado"
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          gap: 1,
        }}
      >
        <Button
          startIcon={<PrintIcon />}
          onClick={handleImprimir}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 1.4,
            borderColor: FP.color.border,
            color: FP.color.textPrimary,
            '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
          }}
        >
          Imprimir
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            borderRadius: 1.4,
            fontWeight: 700,
            bgcolor: FP.color.navy,
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VistaExportacion;
